import { useForm } from '@conform-to/react'
import { getZodConstraint, parseWithZod } from '@conform-to/zod'
import { data, Form } from 'react-router'
import { HoneypotInputs } from 'remix-utils/honeypot/react'
import { z } from 'zod'
import { DynamicInput } from '#app/components/inputWithError.tsx'
import PasswordInput from '#app/components/password-input.tsx'
import { StatusButton } from '#app/components/ui/status-button.tsx'
import { login } from '#app/utils/auth.server.ts'
import { checkHoneypot } from '#app/utils/honeypot.server.ts'
import { useIsPending } from '#app/utils/misc.tsx'
import { type Route } from './+types/login.ts'
import { handleNewSession } from './login.server.ts'

const schema = z.object({
	username: z.string(),
	password: z.string(),
})

export default function Auth({ actionData }: Route.ComponentProps) {
	const [form, fields] = useForm({
		id: 'login-form',
		constraint: getZodConstraint(schema),
		shouldValidate: 'onBlur',
		lastResult: actionData?.result,
		onValidate({ formData }) {
			return parseWithZod(formData, { schema })
		},
		shouldRevalidate: 'onBlur',
	})

	const submitting = useIsPending()

	return (
		<Form
			id={form.id}
			method="POST"
			onSubmit={(e) => form.onSubmit(e)}
			noValidate
			className="flex w-[75%] flex-col gap-4"
		>
			<HoneypotInputs />
			<h2 className="text-secondary-foreground mx-auto -mt-6 text-lg font-bold">
				Login
			</h2>
			<div className="flex flex-col gap-4">
				<DynamicInput
					label="Username"
					name={fields.username.name}
					error={fields.username.errors}
				/>
				<PasswordInput
					label="Password"
					name={fields.password.name}
					error={fields.password.errors}
				/>
			</div>
			<span className="text-xs text-red-500">{form.errors}</span>
			<StatusButton
				className="mt-6"
				type="submit"
				status={submitting ? 'pending' : 'idle'}
				disabled={submitting}
			>
				{submitting ? 'Please wait...' : 'Login'}
			</StatusButton>
		</Form>
	)
}

export async function action({ request }: Route.ActionArgs) {
	const formData = await request.formData()
	await checkHoneypot(formData)
	const submission = await parseWithZod(formData, {
		schema: (intent) =>
			schema.transform(async (data, ctx) => {
				if (intent !== null) return { ...data, session: null }
				const session = await login(data)
				if (!session) {
					ctx.addIssue({
						code: z.ZodIssueCode.custom,
						message: 'Invalid username or password',
					})
					return z.NEVER
				}
				return { ...data, session }
			}),
		async: true,
	})

	if (submission.status !== 'success' || !submission.value.session) {
		return data(
			{ result: submission.reply({ hideFields: ['password'] }) },
			{ status: submission.status === 'error' ? 400 : 200 },
		)
	}

	const { session } = submission.value

	return handleNewSession({
		request,
		session,
	})
}

// export async function loader({ request }: Route.LoaderArgs) {
// 	const user = await isUserExists(request)
// 	if (user) {
// 		return redirect('../permit')
// 	}
// 	return null
// }
